import './MenuBar.scss';

import React, { useState } from 'react';

import { clearUserToken, getInstancePropIdIfUserCan } from '../lib/auth';
import { RouteComponentProps } from 'react-router';
import { User } from '../../shared/types/user';
import { generateCsvReport } from '../lib/api/plugins/sw_export';

import Config from '../lib/config';

import { Dropdown } from '@spark-labs/avocado-ui';
import {
  DropdownChildrenProps,
  DropdownItem,
  DropdownProps,
} from '@spark-labs/avocado-ui/dist/components/Dropdown';
import { LicenseAgreementChecker } from './User/LicenseAgreementChecker';
import { ServerInfoBundle, useServerInfo } from '@go-client/context/ServerInfoContext';
import { BareProperty, Property } from '@go-shared/types/property';
import { BubbleBadge, BubbleButton, BubbleDrawer } from './BubbleUI';
import { PartialBy } from '@go-shared/types/typeUtils';
import { BubbleButtonProps } from './BubbleUI/BubbleButton';
import { useUserInfo } from '@go-client/context/UserInfoContext';

import { partition } from '@go-shared/lib/functional';
import { useNotifications } from '@go-client/context/NotificationsContext';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { safeIdCompare } from '@go-shared/lib/utils';
import ServerAtAGlance from './ServerAtAGlance';
import { getStorage, setStorage } from '@go-client/lib/storage';
import {
  canViewBlueprintEditorDropdownItem,
  canViewUserManagementDropdownItem,
} from '@go-shared/lib/permissions';
import {
  resolveGroupsPath,
  resolveHelpPath,
  resolveItinerariesPath,
  resolveLibrariesPath,
  resolveNotificationsPath,
  resolvePropertiesPath,
  resolveTemplatesPath,
  resolveTranslationsPath,
  ROUTES,
  TRANSLATIONS_SUBNAV_ARRAY,
} from '@go-client/routes';
import { IconName, IconPrefix, IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BareUserNotification } from '@go-shared/types/userNotification';
import {
  ExtensionPointNamespace,
  UIMenuContribution,
} from '@go-shared/types/go-modules/extensionPoints';
import { useModules } from '@go-client/go-modules/core/ModuleContext';
import BugReportForm from './BugReportForm';

interface DropdownBundle {
  options: DropdownItem[];
  handler: (item: DropdownItem) => void;
}

interface MenuItemResources {
  user: User;
  notifications: BareUserNotification[];
  // BareProperty is for backwards compatibility
  property?: Property | BareProperty;
  serverInfo: ServerInfoBundle;
  routeProps: RouteComponentProps;
  currentSailingUrl?: string;
  setShowServerInfo: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setShowBugReport: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}

interface MenuBarBadge {
  key: string;
  number: number;
  variant: 'crimson' | 'navy';
}

interface MenuBarPropsBase {
  property?: Property | BareProperty;
}

interface MenuBarIconBase {
  key: string;
  icon?: IconProp;
  badges?: MenuBarBadge[];
  title?: React.ReactNode;
}

interface MenuBarDropdownIcon {
  type: 'dropdown';
  dropdownProps: Omit<DropdownProps, 'children' | 'alignRight'>;
  buttonProps?: BubbleButtonProps;
}

interface MenuBarLinkIcon {
  type: 'link';
  buttonProps?: BubbleButtonProps;
}

type MenuBarOption = MenuBarIconBase & (MenuBarDropdownIcon | MenuBarLinkIcon);

type MenuBarProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'property'> &
  MenuBarPropsBase &
  RouteComponentProps;

// TODO: replace dropdown items default text with BubbleButton for disabled support
// and better linking capabilitites (enabling right click)

function resolveProperty(
  user: User,
  serverInfo: ServerInfoBundle,
  myProperties: Property[],
  property?: Property | BareProperty
): Property | BareProperty | undefined {
  const fallbackPropertyId = getInstancePropIdIfUserCan(serverInfo, user.role);
  const storage = getStorage();

  if (property) {
    // if we are on a page that has a property, then we can simply use that one
    // to resolve all our menu bar link destinations
    setStorage('lastPropertyId', property._id);
    return property;
  } else {
    // if we are on a page that does not have a property, then we can try and see
    // if we can get a fallback one so that the buttons aren't disabled
    const pId = storage.lastPropertyId || fallbackPropertyId || myProperties[0]?._id;
    const fallbackProperty = myProperties.find(p => safeIdCompare(p._id, pId));
    return fallbackProperty;
  }
}

export default function MenuBar(props: MenuBarProps) {
  const serverInfo = useServerInfo();
  const { myProperties } = usePropertyInfo();
  const { user, refreshContext: refreshUserInfo } = useUserInfo();
  const { notifications } = useNotifications();

  const [showServerInfo, setShowServerInfo] = useState<boolean | undefined>(undefined);

  const [showbugReport, setShowBugReport] = useState<boolean | undefined>(undefined);

  const selectedProperty = resolveProperty(user, serverInfo, myProperties, props.property);

  const {
    property,
    history: historyRouteProp,
    location: locationRouteProp,
    match: matchRouteProp,
    staticContext: staticContextRouteProp,
    className: propClasses,
    ...rest
  } = props;

  const routeProps: RouteComponentProps = {
    history: historyRouteProp,
    location: locationRouteProp,
    match: matchRouteProp,
    staticContext: staticContextRouteProp,
  };

  const storage = getStorage();
  const currentSailingUrl = storage.currentSailingUrl;

  const resources = {
    user,
    notifications,
    property: selectedProperty,
    serverInfo,
    routeProps,
    currentSailingUrl,
    setShowServerInfo,
    setShowBugReport,
  };

  const userMenuBundle = createUserSubMenuItems(resources);

  const menuOptions = createMenuOptions(resources, userMenuBundle);

  const classes = ['menu-bar'];
  if (propClasses) classes.push(propClasses);

  return (
    <>
      <nav {...rest} className={classes.join(' ')}>
        <div className="flex space-x-1">
          {menuOptions.map(mopt => (
            <div key={mopt.key}>
              {mopt.type === 'link' ? (
                makeBubbleButtonFromMenuItem(mopt)
              ) : (
                <Dropdown alignRight {...mopt.dropdownProps}>
                  {childProps => makeBubbleButtonFromMenuItem(mopt, childProps)}
                </Dropdown>
              )}
            </div>
          ))}

          {/*<ExtensionPoint namespace={ExtensionPointNamespace.UI_MENU} />*/}
        </div>
      </nav>
      {!user.acceptedLicenseAgreement && (
        <LicenseAgreementChecker
          onLicenseAgreementAccepted={() => {
            refreshUserInfo({ skipPreferences: true }).catch(console.error);
          }}
        />
      )}
      {showServerInfo && <ServerInfo onClose={() => setShowServerInfo(undefined)} />}
      {showbugReport && <BugReport onClose={() => setShowBugReport(undefined)} />}
    </>
  );
}

type ServerInfoProps = {
  onClose: () => void;
};

const ServerInfo = (props: ServerInfoProps) => {
  return (
    <BubbleDrawer onClose={props.onClose} containerClassName="bg-eggshell">
      <div className="bg-eggshell px-5 pt-5 pb-2">
        <div className="flex items-start ">
          <div className="mr-auto">
            <h2 className="text-2xl text-navy">Instance Details</h2>
          </div>
          <BubbleButton
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div className="mt-10">
          <ServerAtAGlance />
        </div>
      </div>
    </BubbleDrawer>
  );
};

type BugReportProps = {
  onClose: () => void;
};

const BugReport = (props: BugReportProps) => {
  return (
    <BubbleDrawer onClose={props.onClose} containerClassName="bg-eggshell">
      <div className="bg-eggshell px-5 pt-5 pb-2">
        <div className="flex items-start ">
          <div className="mr-auto">
            <h2 className="text-2xl text-navy">Report Issue</h2>
          </div>
          <BubbleButton
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div className="mt-10">
          <BugReportForm />
        </div>
      </div>
    </BubbleDrawer>
  );
};

// this function can generate a button that works under a dropdown or by itself
function makeBubbleButtonFromMenuItem(mopt: MenuBarOption, dcp?: DropdownChildrenProps) {
  // attempt to extract dropdown specific information from parameters
  const dcpToggleRef = dcp ? dcp.toggleRef : undefined;
  const dcpRest = dcp ? { ...dcp } : undefined;

  // dcpRest is everything of DropdownChildrenProps except the toggleRef
  let dcpRestWithoutToggleRef: PartialBy<DropdownChildrenProps, 'toggleRef'> | undefined = dcpRest;

  if (dcpRestWithoutToggleRef) delete dcpRestWithoutToggleRef.toggleRef;

  return (
    <BubbleButton
      buttonRef={dcpToggleRef}
      inverse
      appearance="circled"
      {...(dcpRest || {})}
      {...(mopt.buttonProps || { variant: 'slate' })}
      className={[
        'h-8 p-0-i',
        mopt.type === 'link' ? 'min-w-8 rounded-full' : '',
        mopt.buttonProps ? mopt.buttonProps.className || '' : '',
      ].join(' ')}
      icon={mopt.icon}
      iconNoMargin
    >
      {mopt.buttonProps?.inverse && (
        <>
          {(mopt.badges || []).map(b => (
            <BubbleBadge
              className="text-xs-i p-0"
              style={{ width: 18, height: 18, margin: '0 0.5px' }}
              circle
              variant={b.variant}
              key={b.key}
            >
              {b.number}
            </BubbleBadge>
          ))}
        </>
      )}
      {mopt.title && <span className="ml-1">{mopt.title}</span>}
    </BubbleButton>
  );
}

function createUserSubMenuItems(res: MenuItemResources) {
  const options: DropdownItem[] = [];

  const propIdToUse = res.property ? res.property._id : undefined;

  options.push(
    {
      key: 'logout',
      element: <span className="text-crimson font-semibold">Logout</span>,
    },
    { key: 'about', element: 'About' },
    {
      key: 'help',
      element: 'Help Desk',
    }
    // LABS-2879: Hide Bug Report UI until we improve it
    // { key: 'bugReport', element: 'Report Issue' }
  );

  if (canViewUserManagementDropdownItem(res.user, res.serverInfo)) {
    options.push({
      key: 'user-management',
      element: 'Manage Users',
    });
  }

  // If the SW_EXPORT plugin is active, add an entry to generate an export.
  if (res.serverInfo.plugins.find(p => p.name === 'sw_export') && propIdToUse) {
    options.push({
      key: 'sw_export',
      element: 'Generate Report',
    });
  }

  options.push(
    {
      key: 'kafka-control',
      element: 'Kafka Logs',
    },
    {
      key: 'user-actions',
      element: 'System Logs',
    }
  );

  if (canViewBlueprintEditorDropdownItem(res.user)) {
    options.push({
      key: 'blueprint-editor',
      element: 'Blueprint Editor',
    });
  }

  // options.push({
  //   key: 'manual',
  //   element: 'GO Manual',
  // });

  const handler = async (item: DropdownItem) => {
    switch (item.key) {
      case 'logout':
        clearUserToken();
        setTimeout(() => res.routeProps.history.push('/login'), 100);
        break;
      case 'sw_export':
        if (!propIdToUse) return;
        const result = await generateCsvReport(propIdToUse);
        if (!result.location) return;
        window.open(result.location, '_blank');
        break;
      case 'manual':
        window.open(`${Config.PUBLIC_ASSETS_URL}/manual.pdf`, '_blank');
        break;
      case 'about':
        res.setShowServerInfo(true);
        break;
      case 'itinerary-management':
        res.routeProps.history.push(`/property/${propIdToUse}/itinerary-management`);
        break;
      case 'help':
        res.routeProps.history.push(resolveHelpPath());
        break;
      case 'blueprint-editor':
        res.routeProps.history.push(`/property/${propIdToUse}/feed-blueprint-editor`);
        break;
      case 'bugReport':
        res.setShowBugReport(true);
        break;
      default:
        res.routeProps.history.push(`/tools/${item.key}`);
        break;
    }
  };

  return { options, handler };
}

function createMenuOptions(res: MenuItemResources, userMenuBundle: DropdownBundle) {
  const options: MenuBarOption[] = [];

  const propIdToUse = res.property ? res.property._id : undefined;

  if (res.serverInfo.instanceRole === 'brand') {
    const [prefix, icon, variant, inverse] = resolveMenuIconFromPath(
      'building',
      res.routeProps.match.path
    );

    options.push({
      key: 'goto-properties',
      type: 'link',
      icon: [prefix, icon],
      buttonProps: {
        variant,
        inverse,
        linkProps: {
          to: resolvePropertiesPath(),
        },
      },
    });
  }

  {
    const [prefix, icon, variant, inverse] = resolveMenuIconFromPath(
      'home',
      res.routeProps.match.path
    );

    const inItineraries = ROUTES.ITINERARIES_ROUTE().includes(res.routeProps.match.path);
    const inTemplatesSubpath = [
      ROUTES.TEMPLATE_FEED_ROUTE(),
      ROUTES.TEMPLATE_PLANNER_ROUTE(),
    ].includes(res.routeProps.match.path);

    options.push({
      key: 'goto-home',
      type: 'link',
      icon: [prefix, icon],
      buttonProps: {
        variant,
        inverse,
        disabled: !propIdToUse,
        linkProps: propIdToUse
          ? {
              to:
                inItineraries || inTemplatesSubpath
                  ? resolveTemplatesPath(propIdToUse)
                  : resolveItinerariesPath(propIdToUse),
            }
          : undefined,
      },
    });
  }

  if (res.serverInfo.settings.ALLOW_GROUP_MANAGEMENT) {
    const [prefix, icon, variant, inverse] = resolveMenuIconFromPath(
      'people-group',
      res.routeProps.match.path
    );
    options.push({
      key: 'goto-groups',
      type: 'link',
      icon: [prefix, icon],
      buttonProps: {
        variant,
        inverse,
        disabled: !propIdToUse,
        linkProps: propIdToUse
          ? {
              to: resolveGroupsPath(propIdToUse),
            }
          : undefined,
      },
    });
  }

  {
    const [prefix, icon, variant, inverse] = resolveMenuIconFromPath(
      'books',
      res.routeProps.match.path
    );
    options.push({
      key: 'goto-libraries',
      type: 'link',
      icon: [prefix, icon],
      buttonProps: {
        variant,
        inverse,
        disabled: !propIdToUse,
        linkProps: propIdToUse
          ? {
              to: resolveLibrariesPath(propIdToUse, 'events'),
            }
          : undefined,
      },
    });
  }

  if (propIdToUse && res.serverInfo.languages.length > 0 && res.serverInfo.languages.length > 0) {
    const [prefix, icon, variant, inverse] = resolveMenuIconFromPath(
      'language',
      res.routeProps.match.path
    );

    options.push({
      key: 'goto-translations',
      type: 'link',
      icon: [prefix, icon],
      buttonProps: {
        variant,
        inverse,
        disabled: !propIdToUse,
        linkProps: propIdToUse
          ? {
              to: resolveTranslationsPath(propIdToUse, 'events'),
            }
          : undefined,
      },
    });
  }

  const allNotifs = res.notifications;
  const [highNotifs, lowNotifs] = partition(allNotifs, n => n.tier > 1);
  const notifBadges: MenuBarBadge[] = [];

  if (highNotifs.length) {
    notifBadges.push({
      key: `high-notifs-${highNotifs.length}`,
      number: highNotifs.length,
      variant: 'crimson',
    });
  }

  if (lowNotifs.length) {
    notifBadges.push({
      key: `low-notifs-${lowNotifs.length}`,
      number: lowNotifs.length,
      variant: 'navy',
    });
  }

  const [prefix, icon, variant, inverse] = resolveMenuIconFromPath(
    'bell',
    res.routeProps.match.path
  );

  options.push({
    key: 'goto-notifs',
    type: 'link',
    icon: [prefix, icon],
    badges: notifBadges,
    buttonProps: {
      variant: highNotifs.length > 0 ? 'crimson' : variant,
      inverse,
      linkProps: {
        to: resolveNotificationsPath(),
      },
    },
  });

  if (propIdToUse) {
    const inCanvas = ROUTES.PLANNER_ROUTE().includes(res.routeProps.match.path);
    const inFeed = ROUTES.FEED_ROUTE().includes(res.routeProps.match.path);

    let url = res.currentSailingUrl;

    if (url && inCanvas) {
      url = url.replace('/planner/', '/feed/');
    }

    const [prefix, icon, variant, inverse] = resolveMenuIconFromPath(
      inFeed ? 'newspaper' : 'chart-tree-map',
      res.routeProps.match.path
    );

    options.push({
      key: 'goto-canvas',
      type: 'link',
      icon: [prefix, icon],
      buttonProps: {
        variant,
        inverse,
        disabled: !url,
        linkProps: {
          to: url || '/',
        },
      },
    });
  }

  // This in an exception because we should re write the menu
  // in order to use ExtensionPoints, so as a workaround
  // here we get the modules assigned to the Main Menu extension Point
  // and add it to the menu
  const { getExtensionsByNamespace } = useModules();
  const extensions = getExtensionsByNamespace(ExtensionPointNamespace.UI_MENU);

  if (extensions.length) {
    for (const extension of extensions) {
      const contribution = extension.contribution as UIMenuContribution;

      const to = contribution?.buttonProps?.linkProps?.to?.toString();
      const isActive = to && res.routeProps.match.path.includes(to);
      options.push({
        key: contribution.key,
        type: 'link',
        icon: contribution.icon,
        buttonProps: {
          ...contribution.buttonProps,
          variant: isActive ? 'ocean' : 'slate',
          inverse: !isActive,
        },
      });
    }
  }

  if (userMenuBundle.options.length) {
    options.push({
      key: 'goto-user',
      type: 'dropdown',
      title: (
        <>
          {res.user.firstName} {res.user.lastName}
          <FontAwesomeIcon icon="caret-down" className="ml-2" />
        </>
      ),
      dropdownProps: {
        items: userMenuBundle.options,
        onSelect: userMenuBundle.handler,
      },
    });
  }

  return options;
}

export function resolveMenuIconFromPath(
  icon: IconName,
  currentPath: string
): [IconPrefix, IconName, BubbleButtonProps['variant'], boolean] {
  const checks = [
    icon === 'building' &&
      [
        ROUTES.PROPERTIES_ROUTE(),
        ROUTES.USER_MANAGEMENT_ROUTE(),
        ROUTES.BRAND_SETTINGS_ROUTE(),
      ].includes(currentPath),
    icon === 'home' &&
      [
        ROUTES.ITINERARIES_ROUTE(),
        ROUTES.TEMPLATES_ROUTE(),
        ROUTES.PROPERTY_SETTINGS_ROUTE(),
      ].includes(currentPath),
    icon === 'books' &&
      [
        ROUTES.LIBRARIES_ROUTE('content'),
        ROUTES.LIBRARIES_ROUTE('events'),
        ROUTES.LIBRARIES_ROUTE('feed'),
        ROUTES.LIBRARIES_ROUTE('plans'),
        ROUTES.LIBRARIES_ROUTE('staff'),
        ROUTES.LIBRARIES_ROUTE('venues'),
        ROUTES.ASSETS_ROUTE('images'),
      ].includes(currentPath),
    icon === 'people-group' &&
      [ROUTES.GROUPS_ROUTE(), ROUTES.GROUP_PARTIES_ROUTE()].includes(currentPath),
    icon === 'language' &&
      TRANSLATIONS_SUBNAV_ARRAY.map(e => ROUTES.TRANSLATIONS_ROUTE(e)).includes(currentPath),
    icon === 'bell' && [ROUTES.NOTIFICATIONS_ROUTE()].includes(currentPath),
    icon === 'chart-tree-map' && [ROUTES.PLANNER_ROUTE()].includes(currentPath),
    icon === 'newspaper' && [ROUTES.FEED_ROUTE()].includes(currentPath),
  ];

  const isActive = checks.includes(true);
  const variant =
    icon === 'people-group'
      ? 'forest'
      : icon === 'books' || icon === 'language'
      ? 'slate'
      : icon === 'home' && ROUTES.TEMPLATES_ROUTE().includes(currentPath)
      ? 'velvet'
      : 'navy';

  return [
    ['chart-tree-map', 'newspaper'].includes(icon) ? 'fass' : 'fas',
    icon,
    isActive ? variant : 'slate',
    isActive ? false : true,
  ];
}
